import { useEffect, useRef } from "react";

/**
 * Keyboard shortcuts for a page's primary search field:
 *   - "/"      focuses the search input (unless the user is already typing somewhere)
 *   - "Escape" clears + blurs the input while it is focused
 *
 * @param {React.RefObject<HTMLInputElement>} inputRef  ref attached to the search <input>
 * @param {() => void} onClear  resets the field's state (text, suggestions, …)
 */
export default function useSearchHotkeys(inputRef, onClear) {
  // Keep the latest onClear without re-registering the listener every render.
  const onClearRef = useRef(onClear);
  onClearRef.current = onClear;

  useEffect(() => {
    const isTyping = (el) =>
      !!el &&
      (el.tagName === "INPUT" ||
        el.tagName === "TEXTAREA" ||
        el.isContentEditable);

    const handleKeyDown = (e) => {
      const active = document.activeElement;

      if (
        e.key === "/" &&
        !isTyping(active) &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey
      ) {
        // Don't pull focus to the (background) search field while a modal is open. A
        // closed keepMounted modal stays in the DOM as MuiModal-hidden; it doesn't count.
        if (document.querySelector('.MuiModal-root:not(.MuiModal-hidden), [role="dialog"]')) return;
        e.preventDefault();
        inputRef.current?.focus();
      } else if (e.key === "Escape" && active && active === inputRef.current) {
        onClearRef.current?.();
        inputRef.current?.blur();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [inputRef]);
}
